import { type GroupChannel } from "@sendbird/chat/groupChannel";
import { type BaseMessage } from "@sendbird/chat/message";
import { APP_V2_APP_EVENTS } from "@src/appV2/lib/analytics/events";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTu } from "@src/appV2/i18n/useTu";

interface DeleteMessageParams {
  channel: GroupChannel;
  /**
   * The original Sendbird message instance. Pull this from
   * `ChatMessage.sendbirdMessage` rather than re-fetching from the channel.
   */
  message: BaseMessage;
}

/**
 * Mutation hook to delete a message from a Sendbird channel.
 *
 * @example
 * ```tsx
 * const { mutate: deleteMessage, isPending } = useDeleteMessage();
 *
 * deleteMessage({ channel, message: chatMessage.sendbirdMessage });
 * ```
 */
export function useDeleteMessage() {
  const { tu } = useTu("chat");
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ channel, message }: DeleteMessageParams): Promise<void> => {
      await channel.deleteMessage(message);
    },
    onSuccess: (_, variables) => {
      void queryClient.refetchQueries({
        queryKey: ["chat", "messages", variables.channel.url],
      });
    },
    meta: {
      userErrorMessage: tu("Failed to delete message"),
      logErrorMessage: APP_V2_APP_EVENTS.DELETE_CHAT_MESSAGE_FAILURE,
    },
  });
}
